import { faWarning } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { ChangeEvent, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import Address from '@/components/Address';
import { Color } from '@/constants/color';
import AppContext from '@/contexts/appContext';
import { useRewardCardAccountPreflightValidation } from '@/hooks/useRewardCards';
import PersonContext from '@/pages/Person/PersonContext';
import RewardCardRepository from '@/repositories/RewardCardRepository';
import Button from '@/ui/components/Button';
import DataList, { DataRow } from '@/ui/components/DataList';
import Label from '@/ui/components/Form/Label';
import Modal from '@/ui/components/Modal';
import Spinner from '@/ui/components/Spinner';
import { currencyFormatter } from '@/utils/currencies';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';
import { hasPermission, PEOPLE_ORDER_REWARD_CARDS } from '@/utils/userPermission';

const NewRewardCardButton = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCurrencyWarningOpen, setIsCurrencyWarningOpen] = useState(false);
  const [forwardBalance, setForwardBalance] = useState(true);
  const closeModal = useCallback(() => setIsModalOpen(false), []);
  const closeCurrencyWarning = useCallback(() => setIsCurrencyWarningOpen(false), []);
  const { addMessage } = useContext(AppContext);
  const { person } = useContext(PersonContext);
  const rewardCardRepository = RewardCardRepository();
  const { permissions } = useContext(AppContext);
  const isAllowedNewRewardCard = hasPermission({
    permissions,
    requiredPermission: PEOPLE_ORDER_REWARD_CARDS
  });

  const {
    data: preflightValidationResponse,
    isFetching,
    isRefetching,
    isFetchedAfterMount,
    refetch,
    isSuccess
  } = useRewardCardAccountPreflightValidation(person.id, false);

  useEffect(() => {
    if (isFetchedAfterMount && !isRefetching && isSuccess) {
      setIsModalOpen(true);

      if (preflightValidationResponse?.available_balance_forward_in_cents) {
        setForwardBalance(true);
      }
    }
  }, [preflightValidationResponse, isFetchedAfterMount, isSuccess, isRefetching]);

  const validationFailed = !!preflightValidationResponse?.validation_errors?.length;

  const createRewardCardAccount = useMutation({
    mutationFn: () => {
      return rewardCardRepository.createAccount(person.id, { forward_balance: forwardBalance });
    },
    onSuccess: () => {
      addMessage({ level: 'success', text: 'Successfully created reward card' });
      setIsModalOpen(false);
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });

  const handleForwardBalanceChange = (e: ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setForwardBalance(isChecked);
  };

  const handleOnClick = () => {
    if (person.is_reward_card_eligible) {
      refetch();
    } else {
      setIsCurrencyWarningOpen(true);
    }
  };

  const handleProceedWithCardCreation = useCallback(() => {
    closeCurrencyWarning();
    refetch();
  }, [closeCurrencyWarning, refetch]);

  const handleMutate = useCallback(() => {
    createRewardCardAccount.mutate();
  }, [createRewardCardAccount]);

  const renderModal = useMemo(() => {
    if (validationFailed) {
      return (
        <Modal
          button={{
            color: Color.BLUE,
            size: 'sm',
            children: 'Okay'
          }}
          onClose={closeModal}
          open={isModalOpen}
          textContent={
            <>
              <ul>
                {(preflightValidationResponse?.validation_errors ?? []).map((error, index) => {
                  return (
                    <li key={`${error.code}-${index}`}>
                      <FontAwesomeIcon icon={faWarning} className="text-red-500" /> {error.message}
                    </li>
                  );
                })}
              </ul>
            </>
          }
          title="Unable to create card. You must fix the errors below before proceeding"
        />
      );
    }

    if (preflightValidationResponse) {
      return (
        <Modal
          dismissButton={{
            color: Color.WHITE,
            children: 'I need to fix some things'
          }}
          button={{
            onClick: handleMutate,
            size: 'sm',
            color: Color.GREEN,
            children: 'Create Card',
            disabled: createRewardCardAccount.isPending
          }}
          onClose={closeModal}
          open={isModalOpen}
          textContent={
            <>
              <DataList>
                <DataRow label="Full Name">{preflightValidationResponse.member_full_name}</DataRow>
                <DataRow label="Address">
                  <Address address={preflightValidationResponse.address} showCountry />
                </DataRow>

                {!!preflightValidationResponse.available_balance_forward_in_cents && (
                  <>
                    <DataRow label="Available Balance Forward">
                      <div
                        className={forwardBalance ? 'font-bold text-emerald-700' : 'line-through'}>
                        {currencyFormatter(
                          preflightValidationResponse.available_balance_forward_in_cents / 100
                        )}
                      </div>
                    </DataRow>

                    <Label
                      className="flex items-center text-underline"
                      htmlFor="forward-balance"
                      reverse
                      text="Forward available balance">
                      <input
                        className="mr-1"
                        defaultChecked={forwardBalance}
                        id="forward-balance"
                        onChange={handleForwardBalanceChange}
                        type="checkbox"
                        value="forward-balance"
                      />
                    </Label>
                  </>
                )}
              </DataList>
            </>
          }
          title="Please confirm this is the correct name and address before proceeding"
        />
      );
    }
  }, [
    closeModal,
    createRewardCardAccount.isPending,
    handleMutate,
    forwardBalance,
    isModalOpen,
    preflightValidationResponse,
    validationFailed
  ]);

  const renderCurrencyWarning = useMemo(() => {
    return (
      <Modal
        button={{
          onClick: handleProceedWithCardCreation,
          color: Color.GREEN,
          size: 'sm',
          children: 'Yes'
        }}
        dismissButton={{
          color: Color.WHITE,
          children: 'No'
        }}
        onClose={closeCurrencyWarning}
        open={isCurrencyWarningOpen}
        textContent={
          <>
            <p>
              This person is not in a program that should receive monetary rewards. Are you sure you
              want to create a new reward card?
            </p>
          </>
        }
        title="Currency Warning"
      />
    );
  }, [isCurrencyWarningOpen, closeCurrencyWarning, handleProceedWithCardCreation]);
  const isPending = isFetching || isRefetching;

  return (
    <>
      <Button disabled={isPending || !isAllowedNewRewardCard} onClick={handleOnClick}>
        {isPending ? <Spinner /> : 'New Reward Card'}
      </Button>

      {isModalOpen && renderModal}
      {isCurrencyWarningOpen && renderCurrencyWarning}
    </>
  );
};

export default NewRewardCardButton;
